import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { sendOtp, verifyOtp, login } from "../api/menupro.api";
import { setBusiness } from "../store/menupro.store";
import "./auth.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Verify() {
  const q = useQuery();
  const navigate = useNavigate();

  const phone = q.get("phone") || "";
  const password = q.get("password") || "";

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const code = useMemo(() => digits.join(""), [digits]);

  const inputsRef = useRef([]);

  const [verifying, setVerifying] = useState(false);
  const [seconds, setSeconds] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // redirect if missing phone
  useEffect(() => {
    if (!phone) navigate("/menupro/register");
    // eslint-disable-next-line
  }, []);

  // Timer (60s)
  useEffect(() => {
    if (seconds <= 0) {
      setCanResend(true);
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  // Autofocus first box
  useEffect(() => {
    inputsRef.current?.[0]?.focus?.();
  }, []);

  const focusIndex = (i) => {
    const el = inputsRef.current?.[i];
    if (el) el.focus();
  };

  const onChangeDigit = (i, val) => {
    const v = (val || "").replace(/\D/g, "");
    if (!v) {
      const next = [...digits];
      next[i] = "";
      setDigits(next);
      return;
    }

    // if user types/pastes multiple digits into one field
    const chars = v.split("").slice(0, 6 - i);
    const next = [...digits];
    for (let k = 0; k < chars.length; k++) {
      next[i + k] = chars[k];
    }
    setDigits(next);

    const nextIndex = Math.min(i + chars.length, 5);
    if (i + chars.length <= 5) focusIndex(nextIndex);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = [...digits];
        next[i] = "";
        setDigits(next);
        return;
      }
      // move left if empty
      if (i > 0) {
        focusIndex(i - 1);
        const next = [...digits];
        next[i - 1] = "";
        setDigits(next);
      }
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();

    const chars = text.split("").slice(0, 6);
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < chars.length; i++) next[i] = chars[i];
    setDigits(next);

    focusIndex(Math.min(chars.length, 6) - 1);
  };

  const handleResend = async () => {
    try {
      await sendOtp(phone);
      setSeconds(60);
      setCanResend(false);
      setDigits(["", "", "", "", "", ""]);
      focusIndex(0);
    } catch {
      alert("OTP göndərilmədi");
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      alert("6 rəqəmli OTP daxil edin");
      return;
    }

    try {
      setVerifying(true);
      await verifyOtp(phone, code);

      // ✅ OTP ok -> admin təsdiqini gözlə
      navigate(`/menupro/waiting/${encodeURIComponent(phone)}`);
    } catch (e) {
      const data = e?.response?.data;
      const msg = (typeof data === "string" ? data : data?.message) || e?.message || "Xəta";
      alert(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">MenuPro</h1>
        <p className="auth-subtitle">
          Telefon təsdiqləmə: <b>{phone}</b>
        </p>

        <div className="otp-wrap" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              className="otp-box"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => onChangeDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
            />
          ))}
        </div>

        <button
          className="auth-button"
          onClick={handleVerify}
          disabled={verifying}
          style={{ marginTop: 18 }}
        >
          {verifying ? "Yoxlanılır..." : "Təsdiqlə"}
        </button>

        <p style={{ marginTop: 14, fontSize: 14 }}>
          {canResend ? (
            <button
              onClick={handleResend}
              style={{
                background: "transparent",
                border: "none",
                color: "#DE1857",
                fontWeight: 700,
                cursor: "pointer",
                padding: 0,
              }}
            >
              OTP-ni yenidən göndər
            </button>
          ) : (
            <span style={{ color: "#888" }}>
              Yenidən göndərmək üçün {seconds}s gözləyin
            </span>
          )}
        </p>

        <p className="auth-link">
          <Link to="/menupro/login">Girişə qayıt</Link>
        </p>
      </div>
    </div>
  );
}

